import { isRecord } from "../types/data";

const CHUNK_RELOAD_PREFIX = "cineparagon:chunk-reload:";

const getText = (value: unknown) => {
  if (!value) return "";
  if (typeof value === "string") return value;
  if (!isRecord(value)) return "";
  return [value.name, value.message, value.stack].filter(Boolean).join(" ");
};

const extractChunkSignature = (error: unknown, source = "") => {
  const text = `${getText(error)} ${source}`;
  const urlMatch = text.match(/https?:\/\/[^\s)"']+\/static\/(?:js|css)\/[^\s)"']+/i);
  if (urlMatch) return urlMatch[0];

  const pathMatch = text.match(/\/static\/(?:js|css)\/[^\s)"']+/i);
  if (pathMatch) return pathMatch[0];

  const chunkMatch = text.match(/chunk\s+([a-z0-9_-]+)/i);
  return chunkMatch ? `chunk:${chunkMatch[1]}` : "unknown";
};

export const isChunkLoadError = (error: unknown, source = "") => {
  const text = `${getText(error)} ${source}`.toLowerCase();
  const isStaticAsset = /\/static\/(?:js|css)\//i.test(text);

  return (
    text.includes("chunkloaderror") ||
    text.includes("loading chunk") ||
    text.includes("loading css chunk") ||
    (text.includes("unexpected token '<'") && isStaticAsset)
  );
};

export const recoverFromChunkLoadError = (error: unknown, source = "") => {
  if (typeof window === "undefined" || !isChunkLoadError(error, source)) {
    return false;
  }

  const signature = extractChunkSignature(error, source);
  const storageKey = `${CHUNK_RELOAD_PREFIX}${signature}`;

  try {
    if (sessionStorage.getItem(storageKey)) {
      return false;
    }
    sessionStorage.setItem(storageKey, String(Date.now()));
  } catch {
    // If sessionStorage is blocked, a single reload is still better than a dead route.
  }

  window.setTimeout(() => {
    window.location.reload();
  }, 50);

  return true;
};

export const lazyWithChunkRecovery = <T>(loader: () => Promise<T>) => () =>
  loader().catch((error: unknown) => {
    if (recoverFromChunkLoadError(error)) {
      return new Promise<T>(() => {});
    }
    throw error;
  });

export const installChunkLoadRecovery = () => {
  if (typeof window === "undefined") return;

  window.addEventListener("unhandledrejection", (event) => {
    if (recoverFromChunkLoadError(event.reason)) {
      event.preventDefault();
    }
  });

  window.addEventListener(
    "error",
    (event) => {
      const target = event.target as (EventTarget & { src?: string; href?: string }) | null;
      const source = event.filename || target?.src || target?.href || "";
      const error: unknown = event.error || event.message || event;
      if (recoverFromChunkLoadError(error, source)) {
        event.preventDefault();
      }
    },
    true
  );
};
